import React, { useEffect, useState, useCallback } from 'react';
import cls from 'classnames';
import { Skeleton } from '@byte-design/ui';
import userApis from '@/apis/userApis';
import { AgreementID } from '@/containers/BusinessRelatedParams/consts';
import { PROJECT_PREFIX } from '@/../config/mpa-config';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { Agreement } from '@ad/ad_utils/src/services/ttcx/bam-auto-generate/ad.site.creative_tcpp_server_i18n/namespaces/tcpp_common';
import { UserRoleEnum, globalInfoStore } from '@/models/global';
import DOMPurify from 'dompurify';
// These are currently not on Starling due to language issues
const getFromContentAgreementIds = [
  AgreementID.SOW,
  AgreementID.FrameWorkSOWAPACJP,
  AgreementID.FrameWorkSOWAPACKR,
  AgreementID.FrameWorkSOWLATAMBR,
  AgreementID.FrameWorkSOWLATAMMX3SA,
  AgreementID.SharedFolderWithRevShare,
  AgreementID.SharedFolderWithoutRevShare,
  AgreementID.SubscriptionAddendumForPartner
];

function AgreementView(props: { loading?: boolean; agreement?: Agreement; className?: string }) {
  const { agreement, loading = false } = props;
  const [t] = useTranslation();

  const getHtmlContent = (agreement) => {
    const idNumber = Number(agreement?.agreementId);
    if (getFromContentAgreementIds.includes(idNumber)) {
      return agreement?.content || '';
    }
    return t(agreement?.contentKey as string, 'ttcx') || '';
  };

  return (
    <div className={cls(styles.agreementContent, props.className)}>
      <Skeleton loading={loading} avatar={false} active>
        {/* {!hideTitle() && <div className={styles.agreementTitle}>{agreement?.title}</div>} */}
        {/* eslint-disable-next-line react/no-danger */}
        <div
          className={styles.agreementText}
          dangerouslySetInnerHTML={{
            // @ts-ignore
            ignoreFilterXss: true,
            __html: getHtmlContent(agreement)
          }}
        />
      </Skeleton>
    </div>
  );
}

function AgreementFromTcc(props: { id: AgreementID }) {
  const [agreement, setAgreement] = useState<Agreement>();

  useEffect(() => {
    userApis.getAgreementDetailWithoutLogin({ agreementIds: [props.id] }).then((a) => {
      if (a) {
        setAgreement(a?.agreements?.[props.id]);
      }
    });
  }, [props.id]);

  return <AgreementView loading={!agreement} agreement={agreement} />;
}

function FundedTerms() {
  const [t] = useTranslation();

  return (
    <div className={'flex justify-center text-[#666]'}>
      <div className={'bg-[#f8f9fa] rounded mx-auto my-0 px-6 py-8 text-[14px] leading-[28px]'}>
        <p>
          {t(
            'funded_terms_content_intro',
            'The funded service will be effective in VCP package. To be eligible to receive funded briefs , you must agree to the following criteria:'
          )}
        </p>
        <ul style={{ textAlign: 'initial', marginLeft: '5px' }}>
          <li>
            <div className="flex items-baseline not-italic text-[14px] leading-[28px] w-fit before:bg-[#161823BF] before:relative before:inline-block before:content-['_'] before:w-[3px] before:h-[3px] before:top-[-3px] before:mr-1.5 before:rounded-[100%]">
              {t('funded_terms_content_bullet_1', 'Share full data with the creative partner')}
            </div>
          </li>
          <li>
            <div className="flex items-baseline not-italic text-[14px] leading-[28px] w-fit before:bg-[#161823BF] before:relative before:inline-block before:content-['_'] before:w-[3px] before:h-[3px] before:top-[-3px] before:mr-1.5 before:rounded-[100%]">
              {t(
                'funded_terms_content_bullet_2',
                'Allow TikTok to use creative assets in promotional materials'
              )}{' '}
            </div>
          </li>
          <li>
            <div className="flex items-baseline not-italic text-[14px] leading-[28px] w-fit before:bg-[#161823BF] before:relative before:inline-block before:content-['_'] before:w-[3px] before:h-[3px] before:top-[-3px] before:mr-1.5 before:rounded-[100%]">
              {t('funded_terms_content_bullet_3', 'Submit a review about the partner and platform')}
            </div>
          </li>
          <li>
            <div className="flex items-baseline not-italic text-[14px] leading-[28px] w-fit before:bg-[#161823BF] before:relative before:inline-block before:content-['_'] before:w-[3px] before:h-[3px] before:top-[-3px] before:mr-1.5 before:rounded-[100%]">
              {t('funded_terms_content_bullet_4', 'Respond promptly to ensure on-time creative delivery')}
            </div>
          </li>
          <li>
            <div
              className="flex items-baseline not-italic text-[14px] leading-[28px] w-fit before:bg-[#161823BF] before:relative before:inline-block before:content-['_'] before:w-[3px] before:h-[3px] before:top-[-3px] before:mr-1.5 before:rounded-[100%]"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  `<span>${t(
                    'funded_terms_content_bullet_5',
                    'Test all videos from the brief and spend <span style="color: #D83E0E">correspond minimum spending requirement</span> on selected videos within 30 days of expected video delivery date.'
                  )}</span>`
                )
              }}
            />
          </li>
        </ul>
      </div>
    </div>
  );
}

function IpTransferAgreement() {
  return (
    <div className={styles.agreementContent}>
      <div className={styles.agreementTitle}>TikTok Creative Exchange IP Transfer Agreement </div>
      {/* eslint-disable-next-line react/no-danger */}
      <div className={styles.agreementText}>
        <p>
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          This TikTok Creative Exchange IP Transfer Agreement ("
          <b>Agreement</b>
          {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}
          ") sets forth the legally binding terms between Advertiser and a Creative Partner for the transfer
          of ownership of the Creative Assets (as defined below) commissioned through the TTCX Program unless
          the Advertiser and the Creative Partner otherwise separately agree in writing. Any capitalized terms
          not defined in this Agreement shall have the same meaning as set forth in the TikTok Creative
          Exchange Program Advertiser Terms and the TikTok Creative Exchange Creative Partner Terms
          respectively (collectively “TTCX Terms”). TikTok is not a party to this Agreement. TikTok is
          providing this Agreement as a courtesy to each party, however Creative Partner and Advertiser are
          free to enter into a separate agreement to govern the transfer of the Creative Assets. Any separate
          agreement made between Creative Partner and Advertiser will take precedence over this agreement,
          apart from Section 6 below “Relationship to TikTok”, which shall take precedence over any separate
          agreement.
        </p>
        <ol>
          <li>
            <b>Overview</b>. The TikTok provides the TikTok Creative Exchange Program (“
            <b>TTCX Program</b>
            ”), that connects Advertisers who wish to commission custom video content or other creative
            materials (“
            <b>Creative Assets</b>
            {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}
            ”) from Creative Partners to be used as advertisements on the TikTok Platform (“Platform’). By
            accepting the Creative Brief and delivering the Creative Assets through the TTCX Program, Creative
            Partner is accepting this Agreement and represents and warrants that it has the right, authority,
            and capacity to enter into this Agreement. By submitting a Creative Brief and accepting the
            delivery of Creative Assets through the TTCX Program, Advertiser is accepting this Agreement and
            represents and warrants that it has the right, authority, and capacity to enter into this
            Agreement. This Agreement is effective as of the date of the acceptance of the Creative Assets by
            Advertiser ("
            <b>Effective Date</b>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            ").
          </li>
          <li>
            <b>Assignment</b>
            {/* eslint-disable-next-line max-len */}. Upon acceptance of the Creative Assets by the
            Advertiser, except with respect to the Pre-Existing Materials as set forth below, Creative Partner
            agrees that all right, title, and interest in and to any Advertiser Assets (as defined in the TTCX
            Terms), Creative Assets, and all materials created or developed by Creative Partner or its agents,
            contractors, or representatives (including, without limitation, any Creators) arising out of, or
            in connection with, performing the Services under this Agreement are hereby assigned to Advertiser
            in perpetuity. In addition, Creative Partner shall ensure that, except as otherwise specifically
            agreed in writing, all agreements with Creative Partner’s agents, representatives, or contractors
            provide that such parties convey to Advertiser the required right, title, and interest as required
            herein, including publicity rights. To the extent moral rights (including rights of attribution
            and integrity) cannot be assigned under applicable law, Creative Partner hereby irrevocably waives
            and agrees not to enforce any and all moral rights, to the extent permitted under applicable law.
            Creative Partner agrees to assist in executing any other documents as may be requested by
            Advertiser evidencing, vesting and protecting Advertiser’s sole title and right of ownership in
            all Creative Assets.
          </li>
          <li>
            <b>Pre-Existing Materials</b>
            {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}. Subject to Section 2 above,
            Creative Partner agrees that if, in the course of performing the Services, Creative Partner has
            incorporated into any Creative Assets any pre-existing deliverables, materials, notes, records,
            drawings, designs, products, services, invention, discoveries, original works of authorship,
            developments, improvements, trade secrets, concepts, and/or other proprietary information or
            intellectual property right owned by Creative Partner or in which Creative Partner has an interest
            ("
            <b>Pre-Existing Materials</b>
            {/* eslint-disable-next-line react/no-unescaped-entities,max-len */}
            "): (i) Creative Partner will provide Advertiser with prior written notice of Pre-Existing
            Materials; and (ii) Creative Partner grants Advertiser a nonexclusive, royalty-free, perpetual,
            irrevocable, transferable, worldwide license (with the right to grant and authorize sublicenses)
            to make, have made, use, import, offer for sale, sell, reproduce, distribute, modify, adapt,
            prepare derivative works of, display, perform, and otherwise exploit such Pre-Existing Materials
            in or in connection with such Creative Assets.
          </li>
          <li>
            <b>Representations, Warranties.</b>
            {/* eslint-disable-next-line max-len */}
            Creative Partner represents, warrants, and covenants that (i) it has all rights necessary
            (including any third party licenses required) to grant the foregoing assignment to Advertiser and
            unless disclosed by Creative Partner when initially submitting the Creative Assets to the TTCX
            Program, Creative Partner represents and warrants that the Creative Assets does not contain any
            Pre-Existing Materials or any third party intellectual property; (ii) the Creative Assets comply
            with the General Terms as specified in the TTCX Terms (iii) no Creative Assets will infringe,
            misappropriate or violate any intellectual property or other right of any person or entity; (iv)
            the Creative Assets, will be (a) free of viruses, Trojan horses, trap doors, backdoors, Easter
            eggs, logic bombs, worms, time bombs, cancelbots, and/or other computer programming routine that
            may potentially damage, interfere with, intercept, disable, deactivate, or expropriate any
            Platform or Advertiser system or assets, (b) free from defects in design, materials and
            workmanship, (c) of merchantable quality and fit for the purposes for which they are intended by
            Advertiser, and (d) free and clear of all liens, claims and encumbrances; (iv) Creative Partner
            has the full right to allow it to provide Advertiser with the assignments provided for herein; (v)
            Creative Partner is a validly existing business entity and has all rights, licenses, permits,
            qualifications and consents necessary to perform its and/or their respective obligations pursuant
            to the Agreement.
          </li>
          <li>
            <b>Indemnity</b>
            {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}. Creative Partner will
            defend, indemnify, and hold harmless Advertiser from and against any and all claims, suits,
            actions, costs, expenses, damages, losses, and liabilities based on any claim that the Creative
            Partner infringes or misappropriates a third party's intellectual property rights.
          </li>
          <li>
            <b>Relationship to TikTok</b>
            {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}. Each party agrees that
            TikTok and its affiliates are intended third party beneficiaries of this Agreement. Each party
            hereby releases, and will defend, indemnify, and hold harmless, TikTok and its affiliates from any
            and all claims, suits, actions, costs, expenses, damages, losses, and liabilities related to
            either party's breach of this Agreement.
          </li>
          <li>
            <b>General</b>
            {/* eslint-disable-next-line max-len */}
            .Except as expressly set forth herein, this Agreement represents the entire agreement between the
            parties with respect to the subject matter hereof and supersedes any previous or contemporaneous
            oral or written agreement regarding such subject matter. It is understood that Creative Partner’s
            status hereunder is that of an independent contractor and that no persons employed by Creative
            Partner in performing its obligation hereunder shall be deemed employees of Advertiser. All
            notices may be given by electronic means. If a party waives any term, provision or a party’s
            breach of this Agreement, such waiver shall not be effective unless it is in writing and signed by
            the party against whom such waiver is asserted. No waiver by a party of a breach of this Agreement
            by the other party shall constitute a waiver of any other or subsequent breach by such other
            party. This Agreement may be modified only if authorized representatives of both parties consent
            in writing
          </li>
        </ol>
      </div>
    </div>
  );
}

function AdvertiserTerms() {
  return (
    <div className={styles.agreementContent}>
      <div className={styles.agreementTitle}>TikTok Creative Exchange – Advertiser Terms </div>
      {/* eslint-disable-next-line react/no-danger */}
      <div className={styles.agreementText}>
        <p>Last updated: April 21, 2021</p>
        <p>
          <br />
        </p>
        <p>
          By participating in the TikTok Creative Exchange Program (“
          <b>TTCX Program</b>
          ”) with TikTok (“
          <b>TikTok</b>
          ”), you, the participating advertiser (“
          <b>Advertiser</b>
          ”, “<b>you</b>
          ”, or “<b>your</b>
          ”), agree to these TikTok Creative Exchange Program Advertiser terms and conditions (“
          <b>Terms</b>
          ”), which govern your participation in the TTCX Program through which TikTok will match Advertiser
          with a participating creative partner (“
          <b>Creative Partner</b>
          ”) to create custom content in accordance with a Creative Brief (as defined below) for use by
          Advertiser (each a “<b>Campaign</b>
          ”) solely on the TikTok platform (“
          <b>Platform</b>
          ”). The custom video assets or other creative materials (collectively, “<b>Creative Assets</b>
          ”) to be developed by an applicable Creative Partner will be detailed in one or more creative briefs
          (each a “<b>Creative Brief</b>
          ”), subject to the process set forth below.
        </p>
        <ul>
          {/* eslint-disable-next-line max-len */}
          <li>
            If your usual residence is in the United States, these Terms are entered into by TikTok Inc. (“we”
            or “TikTok”) and the person entering into these Terms electronically (“you” or “Advertiser”).
          </li>
          {/* eslint-disable-next-line max-len */}
          <li>
            If your usual residence is in the European Economic Area, United Kingdom, or Switzerland, these
            Terms are entered into by TikTok Information Technologies UK Limited (“we” or “TikTok”) and the
            person entering into these Terms electronically (“you” or “Advertiser”).
          </li>
          {/* eslint-disable-next-line max-len */}
          <li>
            If your usual residence is not in any of the countries listed above, these Terms are entered into
            by TikTok Pte. Ltd. (“we” or “TikTok”) and the person entering into these Terms electronically
            (“you” or “Advertiser”).{' '}
          </li>
          {/* eslint-disable-next-line max-len */}
          <li>
            If your usual residence is in Brazil, these Terms are entered into by Bytedance Brasil Tecnologia
            Ltda. (“we” or “TikTok”) and the person entering into these Terms electronically (“you” or
            “Advertiser”).
          </li>
        </ul>
        <p>
          These Terms incorporate and are expressly subject to the{' '}
          <a href="https://ads.tiktok.com/i18n/official/policy/disclaimer" target="__blank">
            General TikTok For Business Platform Terms
          </a>
          , the{' '}
          <a href="https://www.tiktok.com/community-guidelines?lang=en#32" target="__blank">
            Community Guidelines
          </a>
          , the{' '}
          <a href="https://ads.tiktok.com/help/article?aid=9550" target="__blank">
            TikTok Advertising Policies - Industry Entry
          </a>
          , and{' '}
          <a href="https://ads.tiktok.com/help/article?aid=9552" target="__blank">
            TikTok Advertising Policies - Ad Creatives & Landing Page
          </a>{' '}
          TikTok Advertising Policies - Ad Creatives & Landing Page, (collectively, the “<b>General Terms</b>
          {/* eslint-disable-next-line max-len */}
          ”). To the extent these Terms conflict with any of the General Terms, these Terms will prevail (but
          only in respect of your participation in the TTCX Program). The General Terms and these Terms
          together will be referred to herein as the “<b>Agreement</b>
          .”
        </p>
        <ol>
          <li>
            <b>Creative Brief Process</b>
            {/* eslint-disable-next-line max-len */}. At the TTCX Home Page, Advertiser may create a new
            Campaign by submitting details of a requested Creative Brief. The Creative Brief may include
            information about Advertiser, relevant industry, the requested Creative Assets, target audience,
            Campaign background, Advertiser objectives, and Campaign timelines. All Creative Briefs are
            subject to TikTok’s review and approval in its sole discretion. If TikTok approves a Creative
            Brief, TikTok will offer eligible Creative Partners the ability to accept Advertiser’s Creative
            Brief, which will begin the Campaign process between Creative Partner and Advertiser as set forth
            in a Creative Brief. Advertiser must review and approve any and all Creative Assets whether in
            draft or final form that are submitted by Creative Partner within three (3) business days of
            receiving such Creative Assets. Advertiser must run the applicable Campaign on the Platform within
            thirty (30) business days of confirming the final Creative Assets. TikTok shall not be responsible
            for any delays or the failure of Creative Partner or Advertiser to meet any deadlines, including,
            without limitation, any delays caused by Advertiser’s failure to timely review and approve
            Creative Assets.
            <ul>
              <li>
                <b>Restrictions</b>
                {/* eslint-disable-next-line max-len */}. If Advertiser’s Creative Brief violates any of the
                terms herein or is otherwise deemed by TikTok as unsuitable for the TTCX Program or the
                Platform, TikTok in its sole discretion, reserves the right to: (i) cancel the applicable
                Campaign or Creative Brief; and/or (ii) prohibit Advertiser from participating in the TTCX
                Program for a thirty (30) day period or another time period as deemed necessary by TikTok.
                Further, Advertiser may only use the Creative Assets in connection with applicable Campaigns
                on the Platform and may not use the Creative Assets in any other media. Violation of this term
                may result in Advertiser’s immediate termination from the TTCX Program.
              </li>
              <li>
                <b>Maximum Limit</b>. Advertiser acknowledges that TikTok may impose a limit on the number of
                Creative Briefs that Advertiser may submit to the TTCX Program.{' '}
              </li>
            </ul>
          </li>
          <li>
            <b>Advertiser Obligations</b>
            {/* eslint-disable-next-line max-len */}. Advertiser wishes to have Creative Assets created for
            distribution on the Platform in connection with one or more of Advertiser’s Campaigns. Advertiser
            authorizes TikTok to facilitate the production of such assets for and on behalf of Advertiser by a
            participating Creative Partner. If Advertiser provides any assets for use in connection with the
            Campaign, including, without limitation, videos, photos, or other visual assets; logos,
            trademarks, and service marks; any actual or simulated likeness, voice, photograph, personal
            characteristic, name, performance; compositions and master recordings of music ”
            <b>Advertiser Assets</b>
            {/* eslint-disable-next-line max-len */}
            ”), Advertiser grants TikTok and Creative Partner the right and license to use the Advertiser
            Assets solely in and in connection with the Creative Assets and as otherwise set forth herein. The
            parties agree, for the avoidance of doubt, that the term “Ad Materials” as used in the General
            Terms shall include the “Advertiser Assets” as used in these Terms.
          </li>
          <li>
            <b>TikTok Obligations</b>
            {/* eslint-disable-next-line max-len */}. TikTok will be responsible for maintaining the TTCX
            Program, which enables participating Creative Partners to view and accept Advertiser-submitted
            Creative Briefs. Notwithstanding anything to the contrary in this Agreement, TikTok makes no
            guarantees or warranties relating to the effectiveness of any Creative Assets or the profile or
            demographics of the Campaign audience, usage statistics or levels of impressions, click through
            rates, page views, consumer responses, impact on sales or other metrics or any other performance
            commitments.
          </li>
          <li>
            <b>Pre-Existing Materials</b>
            {/* eslint-disable-next-line max-len */}. If TikTok or any Creative Partner incorporates any
            pre-existing materials and intellectual property of TikTok or the Creative Partner, such as, but
            not limited to, TikTok effects, content formats, transitions, music from the TikTok Commercial
            Music Library, and filters (“
            <b>Pre-Existing Materials</b>
            {/* eslint-disable-next-line max-len */}
            ”) into the Creative Assets, Advertiser is hereby granted a nonexclusive, royalty-free license to
            the Pre-Existing Materials solely as incorporated in the Creative Assets for use on TikTok and for
            Advertiser’s internal, archival use (“
            <b>Permitted Uses</b>
            {/* eslint-disable-next-line max-len */}
            ”). Any use of the Pre-Existing Materials, as incorporated in the Creative Assets, for any other
            purpose other than the Permitted Uses is subject to the TikTok’s prior written approval.
            Advertiser will also provide TikTok with the specifications and/or all information required for
            the production of the Creative Assets and remains responsible for all representations and
            warranties provided in the General Terms applicable to Advertiser’s use of the Platform.
            Advertiser will review and approve for distribution on the Platform the Creative Assets that were
            created by the Creative Partner. These Terms, do not, and shall not be deemed to, make TikTok an
            agent for Advertiser.
          </li>
          <li>
            <b>Ownership</b>
            {/* eslint-disable-next-line max-len */}. Except with respect to the Pre-Existing Materials as set
            forth below, all right, title, and interest in and to any Advertiser Assets, Creative Assets, and
            all materials created or developed by Creative Partner or its agents, contractors, or
            representatives arising out of, or in connection with, performing the Services under this
            Agreement shall be assigned from Creative Partner to Advertiser subject to the{' '}
            <a href={`https://ads.tiktok.com${PROJECT_PREFIX}/terms/ip-transfer-agreement`}>
              IP Transfer Agreement
            </a>{' '}
            {/* eslint-disable-next-line max-len */}
            when Advertiser accepts the final delivery of the Creative Assets unless Advertiser and Creative
            Partners separately agree in writing on the transfer of ownership. Advertiser agrees that
            Advertiser may only use the Creative Assets in connection with applicable Campaigns on the
            Platform. For clarity, the assignment set forth in the IP Transfer Agreement applies solely to the
            Creative Assets developed by Creative Partner, and does not apply to any patent, TikTok-owned
            Pre-Existing Materials, or other intellectual property rights of TikTok. TikTok reserves all
            rights not expressly granted in this Agreement.
            <p>
              {/* eslint-disable-next-line max-len */}
              TikTok may use any Creative Assets which have been broadcast, published, distributed or
              otherwise made available to the public for purposes of promoting TikTok’s work and/or business,
              including, but not limited to, in client pitches, industry events, sizzle reels, showcases
              (including in connection with promotion of the TTCX Program), marketing campaigns, or other
              TikTok services, websites or properties.
            </p>
          </li>
          <li>
            <b>Tracking</b>
            {/* eslint-disable-next-line max-len */}. Advertiser agrees to implement the conversion tracking
            mechanisms provided or incorporated within the Creative Assets, or requested by TikTok for
            purposes of analyzing any Campaign utilizing the Creative Assets. Advertiser agrees to sharing the
            performance analytics data of Advertiser’s Campaign on the Platform with the Creative Partner.
          </li>
          <li>
            <b>Confidential Information</b>
            {/* eslint-disable-next-line max-len */}. Advertiser agrees that all business, technical and
            financial information (including, without limitation, the identity of and information relating to
            Creative Partners, customers or employees) developed, learned or obtained by or for or on behalf
            of Advertiser during the term of this Agreement that relate to TikTok, Creative Partner or the
            business of TikTok or Creative Partner or that are received by or for TikTok in confidence,
            constitute “<b>Confidential Information</b>
            {/* eslint-disable-next-line max-len */}
            .” Advertiser shall hold in confidence and not disclose or, except in exercising its rights
            hereunder, use any Confidential Information. However, Advertiser shall not be obligated under this
            paragraph with respect to information Advertiser can document is or becomes readily publicly
            available without restriction through no fault of Advertiser.
          </li>
          <li>
            <b>Representations and Warranties by Advertiser</b>
            {/* eslint-disable-next-line max-len */}. Advertiser represents and warrants as follows: (i)
            Advertiser has the full power and authority to enter into this Agreement; (ii) it is the sole
            owner or holder (or the authorized representative of the sole owner or holder) of the rights
            granted herein, including, but not limited to, any and all copyrights, trade names, trademarks,
            logos, symbols, service marks and other proprietary trade designations protected by law associated
            with Advertiser Assets or the applicable Creative Brief; (iii) the consent of no other person or
            entity is required to enable TikTok and Creative Partner to use Advertiser Assets as described in
            the Creative Brief or herein; (iv) the use of Advertiser Assets or the Creative Brief as described
            herein will not violate the rights of any third parties and shall be free and clear of any and all
            claims for royalties, residuals, or other compensation; (v) Advertiser Assets, Campaign(s) and
            applicable Creative Brief(s) will comply with the General Terms, in particular, and without
            limitation, the Community Guidelines, and Advertiser Policies; and (vi) Advertiser shall comply
            with, all applicable laws and TikTok platform rules and policies.
          </li>
          <li>
            <p>
              <b>Indemnification</b>
            </p>
            <ul>
              <li>
                <b>Advertiser</b>
                {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}. Advertiser will defend,
                indemnify and hold harmless TikTok, Creative Partner, and each of its parents, subsidiaries,
                affiliated companies, licensees, successors and assigns, and each of the foregoing entities'
                respective officers, directors, agents, representatives and employees (“
                <b>Indemnitees</b>
                {/* eslint-disable-next-line max-len,react/no-unescaped-entities */}
                ”), from and against any and all third-party claims, actions, damages, liabilities, demands,
                and causes of action (including reasonable attorneys' fees and court costs) (collectively, "
                <b>Claims</b>
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                ") that arise out of or result from Advertiser's breach or alleged breach of this Agreement,
                including any of its representations or warranties set forth herein.
              </li>
              <li>
                <b>TikTok</b>. TikTok will defend, indemnify, and hold Advertiser and its Indemnitees from and
                against any Claims that arise out of TikTok’s breach or alleged breach of this Agreement.
              </li>
              <li>
                <b>Procedure</b>
                {/* eslint-disable-next-line max-len */}. The indemnifying party shall be solely responsible
                for its indemnification obligation set forth herein, subject to the indemnified party’s right
                to (y) participate with counsel of its or their own choosing at its or their own expense; and
                (z) take over such defense. The indemnifying party will not agree to any settlement that
                imposes any obligation or liability on an indemnified party, or that does not provide a full
                release of an indemnified party, without the indemnified party’s prior express written
                consent.
              </li>
            </ul>
          </li>
          <li>
            <b>Termination</b>
            {/* eslint-disable-next-line max-len */}. TikTok may terminate your participation in the TTCX
            Program and/or a Creative Brief at any time, with or without cause, effective immediately upon
            notice to you. TikTok may, in its sole discretion, provide you with an opportunity to cure an
            alleged breach, and if so, you will have the opportunity to cure such alleged breach within the
            time frame provided in TikTok’s notice to you. Further, and for the avoidance of doubt, TikTok may
            terminate the TTCX Program at any time, without any liability to you, and does not guarantee the
            continued existence of the program. The following sections shall survive termination or expiration
            of this Agreement: Confidentiality, Representation and Warranties, Indemnification, and all other
            obligations which by their nature will continue beyond termination or expiration termination or
            expiration.
          </li>
          <li>
            <b>Feedback</b>
            {/* eslint-disable-next-line max-len */}. “Feedback” means any feedback, ideas, comments,
            materials, or suggestions that Advertiser may provide about the TikTok websites, platform or other
            products or services (including the TTCX Program). All Feedback is provided on a voluntary basis.
            If Advertiser provides Feedback, Advertiser grants to TikTok a worldwide, royalty-free,
            non-exclusive, perpetual and irrevocable license to use, copy, modify, transfer, make, have made,
            sell, offer to sell, import, and otherwise exploit the Feedback for any purpose. This may include,
            but is not limited to, incorporating or implementing the Feedback into the TTCX Program.
            Advertiser agrees that TikTok may exploit all Feedback without any restriction or obligation on
            account of intellectual property rights or otherwise.
          </li>
          <li>
            <b>Anti-Corruption and Trade Control</b>
            {/* eslint-disable-next-line max-len */}. Each party agrees that it will comply, and it will
            require that anyone acting on its behalf comply, with all applicable anti-corruption laws and
            rules. Advertiser agrees to comply with all applicable economic and financial sanctions and trade
            embargoes, including those administered or enforced by the U.S. Department of Treasury’s Office of
            Foreign Assets Control (“
            <b>OFAC</b>
            ”) or the U.S. Department of State (“
            <b>Sanctions</b>
            {/* eslint-disable-next-line max-len */}
            ”). Without limiting the foregoing, Advertiser represents, warrants and undertakes that: (i)
            neither Advertiser nor any of its officers, directors, or controlling shareholders, nor to the
            knowledge of Advertiser, any of its agents or employees, is a person (a) whose property or
            interest in property is blocked by any Sanctions, (b) operating, organized, or resident in a
            country or territory that is the target of comprehensive Sanctions (as of the date of this
            Agreement, Cuba, Iran, North Korea, Syria, and the Crimea region, as amended by OFAC from time to
            time) (“
            <b>Sanctioned Countries</b>
            ”), or (c) owned or controlled by any such person (collectively, “<b>Sanctioned Persons</b>
            {/* eslint-disable-next-line max-len */}
            ”); and (ii) Advertiser will not, in connection with its activities involving TikTok or this
            Agreement, conduct any activities (A) to, from, or through – either directly or indirectly – a
            Sanctioned Country or Sanctioned Person in violation of Sanctions applicable to any party to this
            Agreement, (B) without first obtaining all governmental approvals and making all notifications
            that may be required under applicable Sanctions, or (C) that cause TikTok to violate any
            applicable Sanctions. Notwithstanding any other provision of this Agreement, each party
            understands and agrees that this Agreement may be terminated immediately if it or anyone acting on
            its behalf fails to comply with this provision.
          </li>
          <li>
            <b>General</b>
            {/* eslint-disable-next-line max-len */}. Except as expressly set forth herein, this Agreement
            represents the entire agreement between the parties with respect to the subject matter hereof and
            supersedes any previous or contemporaneous oral or written agreement regarding such subject
            matter. TikTok reserves the right to modify or terminate these Terms or the TTCX Program at any
            time, in its sole discretion, without any further liability to you. It is understood that TikTok’s
            and Creative Partner’s status hereunder is that of an independent contractor and that no persons
            employed by TikTok or Creative Partner in performing its obligation hereunder shall be deemed
            employees of Advertiser. Any use of or access to the TikTok services or the Platform will be
            governed solely by separate applicable terms and conditions (and not this Agreement). This
            Agreement does not modify or affect any other agreement you may have entered into with TikTok,
            except that in the event of any direct conflict with the other agreement, this Agreement will
            control. All notices may be given by electronic means.
          </li>
        </ol>
      </div>
    </div>
  );
}

export default AgreementView;

export { FundedTerms, IpTransferAgreement, AdvertiserTerms, AgreementFromTcc };
